"use client";

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker/DatePicker";
import { EntityMultiSelect } from "@/components/EntityMultiSelect/EntityMultiSelect";
import { fetchCompanies } from "@/api/fetchCompanies/fetchCompaniesApi";
import { ApiCompany } from "@/api/fetchCompanies/types";
import { bulkCreateVouchers } from "@/modules/eventRegistration/features/EventVoucherManagement/api/bulkCreateVouchers";
import {
  BulkCreateVoucherResult,
  BulkCreateVouchersResponse,
} from "@/modules/eventRegistration/features/EventVoucherManagement/api/bulkCreateVouchers/types";

interface CompanyEntity {
  id: number;
  name: string;
  companyIdentifier: string;
}

const initialState = {
  baseName: "",
  companies: [] as CompanyEntity[],
  description: "",
  totalSeats: 0,
  isActive: true,
  startDate: null as Date | null,
  endDate: null as Date | null,
};

function BulkCreateVoucher() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState<
    BulkCreateVouchersResponse["data"] | null
  >(null);

  const goToList = () => navigate("/admin/vouchers");

  const startOver = () => {
    setForm(initialState);
    setResults(null);
  };

  const isValid =
    form.baseName.trim().length > 0 &&
    form.companies.length > 0 &&
    Number.isFinite(form.totalSeats) &&
    form.totalSeats >= 0;

  const handleSubmit = () => {
    if (!isValid) return;

    setIsSubmitting(true);
    bulkCreateVouchers({
      baseName: form.baseName.trim(),
      companyIdentifiers: form.companies
        .map((c) => c.companyIdentifier)
        .filter((id) => id.length > 0),
      description: form.description.trim() || null,
      totalSeats: form.totalSeats,
      isActive: form.isActive,
      startDate: form.startDate ? form.startDate.toISOString() : null,
      endDate: form.endDate ? form.endDate.toISOString() : null,
    })
      .then((res) => setResults(res.data))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <MainPageWrapper loading={false} title="Bulk Create Vouchers">
      <div className="max-w-3xl pb-24">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Create one voucher per selected company, all sharing the same
          settings. Each voucher is named &ldquo;
          {form.baseName || "Base name"} &mdash; Company Name&rdquo;.
        </p>

        {results ? (
          <BulkResultReport
            onDone={goToList}
            onStartOver={startOver}
            results={results}
          />
        ) : (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="bulk-base-name">Base Voucher Name</Label>
              <Input
                id="bulk-base-name"
                onChange={(e) =>
                  setForm((f) => ({ ...f, baseName: e.target.value }))
                }
                placeholder="Enter base voucher name..."
                value={form.baseName}
              />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                The company name is appended to this for each voucher.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Companies</Label>
              <EntityMultiSelect<CompanyEntity>
                entityNamePlural="Companies"
                entityNameSingular="Company"
                fetchEntities={async ({ searchTerm, page, pageSize }) => {
                  const response = await fetchCompanies({
                    searchTerm,
                    page,
                    pageSize,
                    sortBy: "companyName",
                    sortOrder: "ASC",
                  });
                  const { data } = response;
                  const totalCount: number =
                    response.meta?.totalCount ?? data.companies.length;

                  return {
                    data: data.companies.map((c: ApiCompany) => ({
                      id: c.id,
                      name: c.companyName,
                      companyIdentifier: c.intacctId ?? "",
                    })),
                    totalCount,
                  };
                }}
                isFullWidth
                onChange={(companies) => setForm((f) => ({ ...f, companies }))}
                renderEntityRow={(ent, isSelected, toggle) => (
                  <div
                    className={`cursor-pointer flex justify-between items-center gap-4 py-3 px-2 border-b last:border-0 border-gray-100 dark:border-slate-700 text-gray-900 dark:text-white ${
                      isSelected ? "bg-blue-50 dark:bg-slate-700" : ""
                    }`}
                    key={ent.id}
                    onClick={() => toggle(ent)}
                  >
                    <span>{ent.name}</span>
                    {isSelected && (
                      <Check className="h-5 w-5 text-primary flex-shrink-0" />
                    )}
                  </div>
                )}
                value={form.companies}
              />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {form.companies.length} compan
                {form.companies.length === 1 ? "y" : "ies"} selected.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bulk-description">Description (Optional)</Label>
              <Textarea
                className="w-full min-h-[75px] h-auto"
                id="bulk-description"
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                placeholder="Enter voucher description..."
                value={form.description}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bulk-total-seats">Number of Seats</Label>
              <Input
                id="bulk-total-seats"
                min={0}
                onChange={(e) =>
                  setForm((f) => ({ ...f, totalSeats: e.target.valueAsNumber }))
                }
                placeholder="Enter number of seats..."
                type="number"
                value={Number.isNaN(form.totalSeats) ? "" : form.totalSeats}
              />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Seats each created voucher provides.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date/Time</Label>
                <DatePicker
                  onChange={(date) =>
                    setForm((f) => ({ ...f, startDate: date }))
                  }
                  placeholder="Pick a start date/time"
                  showTimeSelect
                  value={form.startDate}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date/Time</Label>
                <DatePicker
                  onChange={(date) => setForm((f) => ({ ...f, endDate: date }))}
                  placeholder="Pick an end date/time"
                  showTimeSelect
                  value={form.endDate}
                />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-slate-700 p-4">
              <Label className="text-base">Active</Label>
              <Switch
                checked={form.isActive}
                onCheckedChange={(checked) =>
                  setForm((f) => ({ ...f, isActive: checked }))
                }
              />
            </div>

            <div className="sticky bottom-0 flex justify-end gap-4 px-4 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-950 mt-8">
              <Button onClick={goToList} type="button" variant="outline">
                Cancel
              </Button>
              <Button
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit}
                type="button"
              >
                {isSubmitting
                  ? "Creating..."
                  : `Create ${form.companies.length || ""} Voucher${
                      form.companies.length === 1 ? "" : "s"
                    }`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </MainPageWrapper>
  );
}

function BulkResultReport({
  results,
  onDone,
  onStartOver,
}: {
  results: BulkCreateVouchersResponse["data"];
  onDone: () => void;
  onStartOver: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 text-sm">
        <Badge variant="secondary">{results.created} created</Badge>
        {results.skipped > 0 && (
          <Badge variant="destructive">{results.skipped} skipped</Badge>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-slate-700 divide-y divide-gray-100 dark:divide-slate-700/50">
        {results.results.map((row: BulkCreateVoucherResult) => (
          <div
            className="flex items-start justify-between gap-3 px-3 py-2.5"
            key={`${row.companyIdentifier}-${row.voucherName ?? "na"}`}
          >
            <div className="min-w-0">
              <p className="text-sm font-normal text-gray-900 dark:text-white truncate">
                {row.companyName || row.companyIdentifier}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {row.voucherName ?? row.companyIdentifier}
                {row.reason ? ` — ${row.reason}` : ""}
              </p>
            </div>
            {row.status === "created" ? (
              <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 shrink-0">
                <Check className="h-3.5 w-3.5" /> Created
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-red-500 shrink-0">
                <X className="h-3.5 w-3.5" /> Skipped
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-4">
        <Button onClick={onStartOver} type="button" variant="outline">
          Create More
        </Button>
        <Button onClick={onDone} type="button">
          Done
        </Button>
      </div>
    </div>
  );
}

export default BulkCreateVoucher;
